import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import {
    getTheme,
    isFullscreen,
    toggleFullscreen,
    toggleMiniSidebar,
    toggleMobileSidebar,
    toggleTheme,
} from './layoutUtils'
import useDropdown from './useDropdown'
import api, { API_BASE_URL } from '../../config/api'
import { signOut, getStoredUser, getUserDisplayName } from '../../config/authUtils'

const AnimatedGreeting = () => {
    const [messageIndex, setMessageIndex] = useState(0)
    const [fadeState, setFadeState] = useState('fade-in')

    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours()
        if (hour >= 1 && hour < 12) return 'Good Morning'
        if (hour >= 12 && hour < 17) return 'Good Afternoon'
        return 'Good Evening'
    }

    const greetings = [
        `${getTimeBasedGreeting()}, hope you are having a great day!`,
        "You're doing an amazing job! Keep it up!",
        'Your dedication makes all the difference!',
        'Making progress every day, that is awesome!',
        'Your positive energy is contagious!',
        'Today is a great day to achieve your goals!',
    ]

    useEffect(() => {
        const timeCheckInterval = setInterval(() => {
            greetings[0] = `${getTimeBasedGreeting()}, hope you are having a great day!`
        }, 120000)

        const messageInterval = setInterval(() => {
            setFadeState('fade-out')
            setTimeout(() => {
                setMessageIndex((prev) => (prev + 1) % greetings.length)
                setFadeState('fade-in')
            }, 500)
        }, 6000)

        return () => {
            clearInterval(messageInterval)
            clearInterval(timeCheckInterval)
        }
    }, [])

    const greetingStyle = {
        transition: 'opacity 0.5s ease-in-out',
        opacity: fadeState === 'fade-in' ? 1 : 0,
    }

    return (
        <span className="fs-13 fw-medium" style={greetingStyle}>
            {messageIndex === 0
                ? getTimeBasedGreeting() + greetings[0].substring(getTimeBasedGreeting().length)
                : greetings[messageIndex]}
        </span>
    )
}

const Topbar = () => {
    const [theme, setTheme] = useState(getTheme)
    const [fullscreen, setFullscreen] = useState(isFullscreen)
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [pendingGoalsCount, setPendingGoalsCount] = useState(0)
    const languageDropdown = useDropdown()
    const notificationDropdown = useDropdown()
    const profileDropdown = useDropdown()

    const navigate = useNavigate()
    const storedUser = getStoredUser()
    const baseUrl = API_BASE_URL

    useEffect(() => {
        const handleFullscreenChange = () => setFullscreen(isFullscreen())
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            const token = Cookies.get('token')
            if (!token) {
                navigate('/lockscreen', { replace: true })
            }
        }, 5000)
        return () => clearInterval(interval)
    }, [navigate])

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await api.get('/api/users/me')
                setUser(response.data)
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Failed to fetch profile')
            } finally {
                setLoading(false)
            }
        }
        fetchProfile()
    }, [])

    useEffect(() => {
        let intervalId
        const fetchGoals = async () => {
            try {
                const token = Cookies.get('token')
                const response = await fetch(`${baseUrl}/api/goals/pending`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                })
                const goals = await response.json()
                const pendingGoals = Array.isArray(goals)
                    ? goals.filter((goal) => goal.approval_status === 'pending')
                    : []
                setPendingGoalsCount(pendingGoals.length)
            } catch {
                setPendingGoalsCount(0)
            }
        }
        fetchGoals()
        intervalId = setInterval(fetchGoals, 30000)
        return () => clearInterval(intervalId)
    }, [baseUrl])

    const handleThemeToggle = () => {
        const next = toggleTheme()
        setTheme(next)
    }

    const handleFullscreenToggle = (e) => {
        e.preventDefault()
        toggleFullscreen()
    }

    const handleLogout = () => {
        signOut()
        profileDropdown.close()
        navigate('/login')
    }

    return (
        <header className="navbar-header">
            <div className="page-container topbar-menu">
                <div className="d-flex align-items-center gap-2">
                    <Link to="/" className="logo">
                        <span className="logo-light">
                            <span className="logo-lg">
                                <img src="/assets/img/logo.svg" alt="logo" />
                            </span>
                            <span className="logo-sm">
                                <img src="/assets/img/logo-small.svg" alt="small logo" />
                            </span>
                        </span>
                        <span className="logo-dark">
                            <span className="logo-lg">
                                <img src="/assets/img/logo-white.svg" alt="dark logo" />
                            </span>
                        </span>
                    </Link>

                    <button
                        type="button"
                        id="mobile_btn"
                        className="mobile-btn btn p-0 border-0 bg-transparent d-lg-block"
                        onClick={(e) => {
                            e.preventDefault()
                            if (window.innerWidth >= 992) {
                                toggleMiniSidebar()
                            } else {
                                toggleMobileSidebar()
                            }
                        }}
                        aria-label="Toggle menu"
                    >
                        <i className="ti ti-menu-deep fs-24" />
                    </button>

                    <button
                        type="button"
                        className="sidenav-toggle-btn btn p-0"
                        id="toggle_btn2"
                        onClick={toggleMiniSidebar}
                        aria-label="Toggle sidebar"
                    >
                        <i className="ti ti-chevron-left-pipe" />
                    </button>

                    <div className="ms-2 d-none d-md-block">
                        <AnimatedGreeting />
                    </div>
                </div>

                <div className="d-flex align-items-center">
                    <div className="header-item d-flex d-lg-none me-2">
                        
                    </div>

                    <div className="header-item me-1" ref={notificationDropdown.ref}>
                        <button
                            type="button"
                            className="topbar-link btn btn-icon border-0 bg-transparent position-relative"
                            onClick={notificationDropdown.toggle}
                            aria-label="Notifications"
                        >
                            <i className="ti ti-bell fs-16" />
                            {pendingGoalsCount > 0 && (
                                <>
                                    <span
                                        className="position-absolute top-0 end-0 translate-middle rounded-circle bg-danger"
                                        style={{
                                            width: '10px',
                                            height: '10px',
                                            animation: 'blinker 1s linear infinite',
                                        }}
                                    />
                                    <span className="badge bg-danger rounded-pill position-absolute top-0 end-0 translate-middle-x translate-middle-y fs-10">
                                        {pendingGoalsCount > 9
                                            ? '9+'
                                            : pendingGoalsCount}
                                    </span>
                                </>
                            )}
                        </button>

                        {notificationDropdown.open && (
                            <div className="dropdown-menu dropdown-menu-end show shadow-lg border-0 p-0 overflow-hidden" style={{ minWidth: '320px' }}>
                                <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)' }}>
                                    <h6 className="mb-0 text-white fw-bold">
                                        <i className="ti ti-bell-ringing me-2" />
                                        Notifications
                                    </h6>
                                    {pendingGoalsCount > 0 && (
                                        <span className="badge bg-white text-primary rounded-pill fs-11 px-3">
                                            {pendingGoalsCount} pending
                                        </span>
                                    )}
                                </div>

                                {pendingGoalsCount === 0 ? (
                                    <div className="p-5 text-center text-muted">
                                        <i className="ti ti-bell-off fs-40 d-block mb-2 opacity-50" />
                                        <p className="mb-0 fs-14">No new notifications</p>
                                    </div>
                                ) : (
                                    <div className="py-2" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                        <div className="mx-3 my-2 rounded-3 p-3 d-flex align-items-center justify-content-between gap-3"
                                            style={{
                                                backgroundColor: '#EEF2FF',
                                                border: '1px solid #C7D2FE',
                                            }}
                                        >
                                            <div className="d-flex align-items-start gap-2">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#4F46E5',
                                                        color: '#fff',
                                                    }}
                                                >
                                                    <i className="ti ti-clock-hour-5 fs-16" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="mb-0 fw-semibold fs-14 text-dark">
                                                        {pendingGoalsCount} goal{pendingGoalsCount > 1 ? 's' : ''} pending approval
                                                    </p>
                                                    <p className="mb-0 fs-12 text-muted">
                                                        Action required for pending reviews
                                                    </p>
                                                </div>
                                            </div>
                                            <Link
                                                to="/hr/employees-pending-goals"
                                                className="btn btn-sm flex-shrink-0 text-white fw-semibold fs-12 px-3 shadow-sm"
                                                style={{ backgroundColor: '#4F46E5' }}
                                                onClick={() => notificationDropdown.close()}
                                            >
                                                View
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="header-item">
                        <div className="me-2">
                            <button
                                type="button"
                                className="btn topbar-link border-0 bg-transparent"
                                id="btnFullscreen"
                                onClick={handleFullscreenToggle}
                                aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                            >
                                <i
                                    className={`ti ${
                                        fullscreen ? 'ti-minimize' : 'ti-maximize'
                                    } fs-16`}
                                />
                            </button>
                        </div>
                    </div>

                    <div className="header-item d-none d-sm-flex me-2">
                        <button
                            type="button"
                            className="topbar-link btn btn-icon border-0 bg-transparent"
                            id="light-dark-mode"
                            onClick={handleThemeToggle}
                            aria-label="Toggle theme"
                        >
                            <i
                                className={`ti ${
                                    theme === 'dark' ? 'ti-sun' : 'ti-moon'
                                } fs-16`}
                            />
                        </button>
                    </div>

                    <div className="header-item me-2">
                        
                    </div>

                    <div className="header-item" ref={profileDropdown.ref}>
                        <button
                            type="button"
                            className="btn topbar-link p-0 border-0 bg-transparent d-flex align-items-center gap-2"
                            onClick={profileDropdown.toggle}
                            disabled={loading || !!error || !user}
                            aria-label="Profile menu"
                        >
                            <span
                                className="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                style={{
                                    background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                                    width: '36px',
                                    height: '36px',
                                }}
                            >
                                {loading || error || !user ? (
                                    <i className="ti ti-user fs-16" />
                                ) : (
                                    (user?.employee?.full_name || getUserDisplayName(storedUser) || 'U')
                                        .charAt(0)
                                        .toUpperCase()
                                )}
                            </span>
                            
                            <i className={`ti ti-chevron-down fs-12 text-muted d-none d-md-block ${profileDropdown.open ? 'rotate-180' : ''}`}
                                style={{ transition: 'transform 0.2s ease', transform: profileDropdown.open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            />
                        </button>

                        {profileDropdown.open && (
                            <div className="dropdown-menu dropdown-menu-end show shadow-lg border-0 p-0 overflow-hidden mt-2" style={{ minWidth: '280px' }}>
                                {!loading && !error && user && (
                                    <>
                                        <div className="px-4 py-4 border-bottom" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)' }}>
                                            <div className="d-flex align-items-center gap-3">
                                                <span
                                                    className="avatar avatar-lg rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm flex-shrink-0"
                                                    style={{
                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                        border: '2px solid rgba(255,255,255,0.3)',
                                                        width: '56px',
                                                        height: '56px',
                                                        fontSize: '1.4rem',
                                                    }}
                                                >
                                                    {(user?.employee?.full_name || getUserDisplayName(storedUser) || 'U')
                                                        .charAt(0)
                                                        .toUpperCase()}
                                                </span>
                                                <div className="min-w-0 text-white">
                                                    <h6 className="mb-0 fw-bold fs-15 text-truncate">
                                                        {user?.employee?.full_name || getUserDisplayName(storedUser)}
                                                    </h6>
                                                    <p className="mb-0 fs-12 text-white-50 text-truncate">
                                                        {user?.email || storedUser?.email}
                                                    </p>
                                                    <span
                                                        className="badge mt-1 fs-11 fw-medium px-3 py-1 rounded-pill"
                                                        style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                                                    >
                                                        {user?.employee?.position || storedUser?.role_display || 'HR Manager'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="py-2">
                                            <Link
                                                to="/hr/profile"
                                                className="dropdown-item d-flex align-items-center gap-3 px-4 py-2 fs-14 text-dark hover:bg-indigo-50"
                                                onClick={() => profileDropdown.close()}
                                                style={{ transition: 'background 0.2s ease' }}
                                            >
                                                <i className="ti ti-user-circle fs-18 text-primary" />
                                                My Profile
                                            </Link>
                                            <Link
                                                to="/hr/change-password"
                                                className="dropdown-item d-flex align-items-center gap-3 px-4 py-2 fs-14 text-dark"
                                                onClick={() => profileDropdown.close()}
                                            >
                                                <i className="ti ti-lock fs-18 text-primary" />
                                                Change Password
                                            </Link>
                                            

                                            <div className="dropdown-divider mx-3 my-2" />

                                            <button
                                                type="button"
                                                className="dropdown-item d-flex align-items-center gap-3 px-4 py-2 fs-14 fw-medium w-100 text-start"
                                                onClick={handleLogout}
                                                style={{ color: '#dc3545' }}
                                            >
                                                <i className="ti ti-logout-2 fs-18" />
                                                Logout
                                            </button>
                                        </div>
                                    </>
                                )}

                                {(loading || error) && (
                                    <div className="px-4 py-5 text-center text-muted">
                                        {loading && (
                                            <>
                                                <div className="spinner-border spinner-border-sm text-primary mb-2" />
                                                <p className="mb-0 fs-13">Loading profile...</p>
                                            </>
                                        )}
                                        {error && (
                                            <>
                                                <i className="ti ti-alert-triangle fs-36 d-block mb-2 text-danger" />
                                                <p className="mb-0 fs-13 text-danger">Failed to load profile</p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes blinker {
                    50% { opacity: 0.3; }
                }
                .dropdown-item:active {
                    background-color: #EEF2FF;
                    color: #4F46E5;
                }
                .dropdown-item:hover {
                    background-color: #F5F3FF;
                }
            `}</style>
        </header>
    )
}

export default Topbar
